/**
 * Deep equality check for objects and arrays
 * Handles null, undefined, dates, and nested structures
 */
export function isDeepEqual(a: any, b: any): boolean {
  if (a === b) return true;

  // Handle null/undefined
  if (a == null || b == null) return a == b;

  // Handle dates
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => isDeepEqual(item, b[index]));
  }

  // Handle objects
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    return keysA.every((key) => isDeepEqual(a[key], b[key]));
  }

  return false;
}

/**
 * Normalize a value for comparison
 * Treats null, undefined, and empty string as equivalent
 */
export function normalizeValue<T>(value: T | null | undefined, defaultValue?: T): T | null {
  if (value === null || value === undefined) return defaultValue ?? null;
  if (typeof value === 'string' && value.trim() === '') return defaultValue ?? null;
  return value;
}

/**
 * Normalize string values for comparison
 * Trims whitespace and treats empty strings as null
 */
export function normalizeString(value: string | null | undefined): string | null {
  if (!value || value.trim() === '') return null;
  return value.trim();
}

/**
 * Compare two values with normalization
 */
export function areValuesEqual<T>(a: T, b: T, normalizer?: (value: T) => T): boolean {
  if (normalizer) {
    return isDeepEqual(normalizer(a), normalizer(b));
  }
  return isDeepEqual(a, b);
}
